import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Flex,
  Heading,
  Link,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getCustomerConfig, getTenantByHost, getTenantId, TenantId, tenants } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import {
  accountTypeOptions,
  businessTypeOptions,
  getContactOptions,
  notificationCategoryOptions,
  PermissionLevel,
  prettyDate,
} from '@pelicargo/utils';
import { AccountType, BusinessType, NotificationCategory, ProviderAccount } from '@prisma/client';
import { uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { useAccountInvite } from '../../hooks/accounts/useAccountInvite';
import { addressOptional } from '../../hooks/quote/useAddressForm';
import { useDashboard } from '../../hooks/useDashboard';
import { AccountWebhooksSection } from '../AccountWebhooks/AccountWebhooksSection';
import { ApiKeysSection } from '../ApiKeys/ApiKeysSection';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { BaseDrawer } from '../BaseDrawer';
import { AddressForm } from '../Forms/AddressForm';
import { FulfillmentPartnerConfigurationsForm } from '../Forms/FulfillmentPartnerConfigurationsForm';
import { PermissionForm } from '../Forms/PermissionsForm';
import { Label, LabeledText } from '../LabeledText';
import { NetworksSelect } from '../NetworksSelect';
import { ReactSelect } from '../ReactSelect';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleSwitch } from '../SimpleSwitch';
import { SimpleTagsInput } from '../SimpleTagsInput';
import { SimpleTextarea } from '../SimpleTextarea';

const schema = z
  .object({
    name: z.string().min(1, 'Required'),
    iata_number: z.string().nullish(),
    account_type: z.nativeEnum(AccountType, {
      errorMap: () => ({
        message: 'Required',
      }),
    }),
    business_type: z.nativeEnum(BusinessType, {
      errorMap: () => ({
        message: 'Required',
      }),
    }),
    address: addressOptional,
    parent_id: z.string().nullish(),
    is_verified: z.boolean().nullish(),
    highlight_until: z.coerce.date().nullish(),

    is_iata_number_verified: z.boolean().nullish(),

    is_open_to_domain_whitelist: z.boolean().nullish(),
    domain_whitelist: z.array(z.string()).nullish(),
    networks: z.array(z.string()).nullish(),
    notes: z.string().nullish(),
    default_contacts: z
      .array(
        z.object({
          contact_id: z.coerce.number(),
          updates_for: z.array(z.nativeEnum(NotificationCategory)),
        }),
      )
      .optional(),
    provider_accounts: z
      .array(
        z.object({
          provider_internal_account_id: z.string().nullish(),
          provider_id: z.string(),
          id: z.coerce.number(),
          is_blacklisted: z.boolean().optional(),
          has_account: z.boolean().optional(),
        }),
      )
      .optional(),
    provider_id: z.string().nullish(),
    flags: z.array(z.string()),
    account_tenant_accesses: z.array(
      z.object({
        tenant_id: z.enum(Object.keys(tenants) as [TenantId, ...TenantId[]]),
        can_edit: z.boolean().default(false),
      }),
    ),
  })
  .superRefine((data) => {
    // If the account type is airline, the provider_id is required
    if (data.business_type === BusinessType.AIRLINE) {
      return z.object({
        provider_id: z.string(),
      });
    }
  });

type FormValues = z.infer<typeof schema>;

type InviteFormValues = {
  email: string;
};

type Props = {
  accountId?: number | null;
  isOpen: boolean;
  onClose: () => void;
};

const BLANK_VALUES: FormValues = {
  name: '',
  iata_number: '',
  account_type: undefined,
  business_type: undefined,
  address: {
    city: '',
    country: '',
    recipient: '',
    line_one: '',
    line_two: '',
    zip_code: '',
    state: '',
  },

  parent_id: '',
  is_open_to_domain_whitelist: false,
  domain_whitelist: [],
  networks: [],
  notes: '',
  provider_id: '',
  default_contacts: [],
  provider_accounts: [],
  highlight_until: undefined,
  is_iata_number_verified: false,
  is_verified: false,
  flags: [],
  account_tenant_accesses: [
    {
      tenant_id: getTenantId(),
      can_edit: true,
    },
  ],
};

const AccountTenantField = ({ prefix }: { prefix: string }) => {
  const { knownTenants } = useAuth();
  const value = useWatch({
    name: `${prefix}.tenant_id`,
  });
  const allTenants = useMemo(() => {
    return uniq([...knownTenants, value]).filter(Boolean);
  }, [value, knownTenants]);

  return (
    <Flex justifyItems="center" gap={3}>
      <SimpleSelect
        name={`${prefix}.tenant_id`}
        options={allTenants.map((id) => ({
          label: id,
          value: id,
        }))}
      />
      <SimpleCheckbox alignContent="center" label="Can be edited by tenant" name={`${prefix}.can_edit`} />
    </Flex>
  );
};

export const AccountFormDrawer = ({ accountId, isOpen, onClose }: Props) => {
  const toast = useToast();
  const methods = useForm({
    defaultValues: BLANK_VALUES,
    resolver: zodResolver(schema),
  });
  const { permission } = useAuth();
  const { isDevMode } = useDashboard();
  const networksConfig = useMemo(() => getCustomerConfig().networks, []);
  const networkOptions = useMemo(
    () => (networksConfig?.options ?? []).map((network) => ({ label: network, value: network })),
    [networksConfig],
  );
  const collectNetworks = !!networksConfig;

  const inviteMethods = useForm();
  const [prices, setPrices] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const showApiTab = !!accountId && permission === PermissionLevel.SUPERADMIN && isDevMode;

  const { data: canEdit } = trpc.canEditAccount.useQuery({ id: accountId }, { enabled: !!accountId });
  const canSave = !accountId || (canEdit && !!accountId);

  const { data: allFlags } = trpc.getAllAccountFlags.useQuery();
  const { data } = trpc.getAccount.useQuery({ id: accountId as number }, { enabled: !!accountId });
  const { data: allAccounts } = trpc.getAllAccounts.useQuery(undefined);
  const { data: allProviders } = trpc.getAllProviders.useQuery(undefined);
  const { mutateAsync: createAccount, isLoading: isCreating } = trpc.createAccount.useMutation();
  const { mutateAsync: updateAccount, isLoading: isUpdating } = trpc.updateAccount.useMutation();
  const { data: accountInvites, refetch: refetchInvites } = trpc.getAccountInvites.useQuery(
    { accountId: accountId as number },
    { enabled: !!accountId },
  );
  const { createAccountInvite, isLoading: isInviting } = useAccountInvite();

  const { data: products } = trpc.getProducts.useQuery(null, {
    enabled: !!accountId,
  });
  const {
    data: checkoutSession,
    refetch: generateCheckoutSessionToken,
    isRefetching: isGeneratingToken,
    error: checkoutSessionError,
  } = trpc.generateCheckoutSessionToken.useQuery(
    { id: accountId, price_ids: prices.map((price) => price.value) },
    { enabled: !accountId && tabIndex === 4 },
  );

  const { data: contactsFromAccount } = trpc.getContactsByAccountId.useQuery(
    { id: accountId },
    { enabled: !!accountId && tabIndex === 5 },
  );

  useEffect(() => {
    if (!showApiTab) setTabIndex(0);
  }, [showApiTab]);

  useEffect(() => {
    if (!accountId) {
      methods.reset(BLANK_VALUES);
    }
    if (accountId) {
      methods.reset({
        name: data?.name,
        iata_number: data?.iata_number,
        business_type: data?.business_type,
        address: data?.address,
        account_type: data?.type,
        parent_id: String(data?.parent_id),
        is_verified: !!data?.verified_at,
        is_iata_number_verified: data?.is_iata_number_verified,
        highlight_until: data?.highlight_until,
        provider_accounts: data?.provider_accounts?.map((providerAccount: ProviderAccount) => ({
          id: providerAccount.id,
          provider_id: providerAccount.provider_id,
          is_blacklisted: providerAccount.is_blacklisted,
          provider_internal_account_id: providerAccount.provider_internal_account_id,
          has_account: providerAccount.has_account,
        })),
        domain_whitelist: data?.domain_whitelist,
        networks: data?.networks ?? [],
        notes: data?.notes,
        is_open_to_domain_whitelist: data?.is_open_to_domain_whitelist,
        provider_id: data?.provider_id,
        default_contacts: data?.default_contacts?.map((contact) => ({
          contact_id: contact.contact_id,
          updates_for: contact.updates_for,
        })),
        flags: data?.account_flags?.map((flag) => flag.flag_name) || [],
        account_tenant_accesses: data?.account_tenant_accesses ?? [],
      });
    }
  }, [data, accountId, methods]);

  const copy = useMemo(() => {
    return accountId ? 'Update Account' : 'Create Account';
  }, [accountId]);

  const handleTabsChange = (nextIndex: number) => {
    setTabIndex(nextIndex);
  };

  const handleClose = useCallback(() => {
    methods.reset(BLANK_VALUES);
    setTabIndex(0);
    onClose();
  }, [methods, onClose]);

  const onValid = useCallback(
    async (values: FormValues) => {
      try {
        if (accountId) {
          await updateAccount({
            ...values,
            id: accountId,
            parent_id: values.parent_id ? Number(values.parent_id) : undefined,
            verified_at: values.is_verified ? new Date() : undefined,
            provider_id: values.provider_id ? values.provider_id : undefined,
            default_contacts:
              values?.default_contacts?.map((contact) => ({
                contact_id: Number(contact.contact_id),
                updates_for: contact.updates_for,
              })) || [],
            provider_accounts:
              values?.provider_accounts?.map((data) => ({
                provider_internal_account_id: data.provider_internal_account_id,
                provider_id: data.provider_id,
                id: data.id,
                is_blacklisted: data.is_blacklisted,
                has_account: data.has_account,
              })) || [],
          });
        }

        if (!accountId) {
          await createAccount({
            ...values,
            parent_id: values.parent_id ? Number(values.parent_id) : undefined,
            verified_at: values.is_verified ? new Date() : undefined,
            provider_id: values.provider_id ? values.provider_id : undefined,
            provider_accounts:
              values?.provider_accounts?.map((data) => ({
                provider_internal_account_id: data.provider_internal_account_id,
                provider_id: data.provider_id,
                id: data.id,
                is_blacklisted: data.is_blacklisted,
                has_account: data.has_account,
              })) || [],
          });
        }

        handleClose();
        toast({
          title: 'Success',
          description: accountId ? 'Account updated' : 'Account created',
          status: 'success',
        });
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      }
    },
    [accountId, handleClose, toast, updateAccount, createAccount],
  );

  const handleSubmit = methods.handleSubmit(onValid);

  const onCreateInvite = async (values: InviteFormValues) => {
    try {
      await createAccountInvite({
        account_id: accountId,
        email: values.email as string,
      });
      inviteMethods.reset({ email: '' });
      refetchInvites();
      toast({
        title: 'Success',
        description: `An invite has been sent to ${values.email}`,
        status: 'success',
      });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const handleCreateInvite = inviteMethods.handleSubmit(onCreateInvite);

  const handleOnProductChange = (values: any) => {
    setPrices(values);
  };

  useEffect(() => {
    if (checkoutSessionError) {
      toast({
        title: 'Error',
        description: checkoutSessionError.message,
        status: 'error',
      });
    }
  }, [checkoutSessionError, toast]);

  const contactOptions = getContactOptions(contactsFromAccount || []);

  return (
    <BaseDrawer isOpen={isOpen} onClose={handleClose}>
      <DrawerHeader>
        <Heading variant="h4">{copy}</Heading>
      </DrawerHeader>
      <DrawerBody p="0">
        <Tabs index={tabIndex} onChange={handleTabsChange} colorScheme="slate" isLazy>
          <TabList>
            <Tab>Account</Tab>
            <Tab isDisabled={!accountId}>Invites</Tab>
            <Tab isDisabled={!accountId}>Users</Tab>
            <Tab isDisabled={!accountId}>Markups</Tab>
            <Tab isDisabled={!accountId || !getTenantByHost().isSystemTenant}>Billing</Tab>
            <Tab isDisabled={!accountId}>Notifications</Tab>
            <Tab isDisabled={!accountId}>Provider Accounts</Tab>
            <Show if={showApiTab}>
              <Tab>API</Tab>
            </Show>
          </TabList>
          <TabPanels>
            <TabPanel>
              <SimpleForm {...methods} submitOnBlur={false}>
                <Stack w="full" h="full" spacing="2">
                  <SimpleInput name="name" label="Name" />
                  <SimpleInput name="iata_number" label="IATA" />

                  <SimpleSelect name="account_type" label="Account Type" options={accountTypeOptions} />
                  <SimpleSelect name="business_type" label="Business Type" options={businessTypeOptions} />
                  <Show if={collectNetworks}>
                    <NetworksSelect name="networks" options={networkOptions} />
                  </Show>
                  <AddressForm name="address" />

                  <SimpleReactSelect
                    name="parent_id"
                    label="Parent Account"
                    isSearchable
                    options={allAccounts?.map((account) => ({
                      label: account?.name,
                      value: String(account?.id),
                    }))}
                  />

                  <AirlineInput
                    name="provider_id"
                    label="Airline"
                    placeholder="Airline Account is Associated To"
                    includeNullOption
                  />

                  <Label label="Workspace Domains" />
                  <SimpleTagsInput name="domain_whitelist" placeholder="Ex. example.com" />

                  <Stack direction="row" spacing="6" align="center">
                    <SimpleCheckbox name="is_open_to_domain_whitelist" label="Workspace Open to Specified Domains" />
                    <SimpleCheckbox name="is_verified" label="Verified Account Type" />
                    <SimpleCheckbox name="is_iata_number_verified" label="Verified Company" />
                    <SimpleLabeledPopoverInput
                      name="highlight_until"
                      label="Highlight Until"
                      inputType="date-picker"
                      showTime={false}
                      showClearButton
                    />
                  </Stack>
                  <SimpleReactSelect
                    name="flags"
                    placeholder="Put flags here"
                    isMulti
                    type="creatable"
                    options={allFlags?.map((tag) => ({
                      label: tag,
                      value: tag,
                    }))}
                  />
                  <SimpleTextarea label="Notes" placeholder="Notes" name="notes" rows={8} />
                  <Show if={getTenantByHost().isSystemTenant || permission === PermissionLevel.SUPERADMIN}>
                    <Label label="Tenant Access" />
                    <SimpleFieldArray
                      form={(args) => <AccountTenantField prefix={args.prefix} />}
                      name="account_tenant_accesses"
                      canAdd
                      canRemove
                    />
                  </Show>
                </Stack>
              </SimpleForm>
            </TabPanel>
            <TabPanel>
              <Stack>
                {accountInvites?.map((invite) => (
                  <Card key={invite?.id}>
                    <CardBody>
                      <Stack direction="row" align="center" justify="space-between">
                        <LabeledText label={invite?.email} value={prettyDate(invite?.accepted_at)} />
                      </Stack>
                    </CardBody>
                  </Card>
                ))}
              </Stack>
            </TabPanel>
            <TabPanel>
              <PermissionForm accountId={accountId} />
            </TabPanel>
            <TabPanel>
              <FulfillmentPartnerConfigurationsForm accountId={accountId} />
            </TabPanel>
            <TabPanel>
              <Stack direction="column" spacing={4}>
                <Heading variant="h5">Billing</Heading>
                <LabeledText
                  label="Stripe Customer"
                  value={
                    <Link
                      href={`https://dashboard.stripe.com/customers/${data?.stripe_customer_id}`}
                      target="_blank"
                      isExternal
                    >
                      {data?.stripe_customer_id}
                      <ExternalLinkIcon mx="1.5" mb={1} />
                    </Link>
                  }
                />
                <Stack spacing="4" direction="row" align="end">
                  <Stack flex="1">
                    <Label label="Available products" />
                    <ReactSelect
                      name="product"
                      value={prices}
                      options={products?.map((product) => ({
                        label: product.product,
                        options: product.prices.map((price) => ({
                          label: `$${price.unit_amount / 100}/month`,
                          value: price.id,
                        })),
                      }))}
                      onChange={handleOnProductChange}
                      isMulti
                    />
                  </Stack>
                  <Button onClick={() => generateCheckoutSessionToken()} isLoading={isGeneratingToken}>
                    Generate Checkout Session
                  </Button>
                </Stack>
                <LabeledText
                  label="Checkout Session"
                  value={checkoutSession || 'Select a product to generate a link'}
                  textProps={{ maxW: 'lg' }}
                  isCopyable={!!checkoutSession}
                />
              </Stack>
            </TabPanel>
            <TabPanel>
              <SimpleForm {...methods} submitOnBlur={false}>
                <Heading variant="h5">Notifications</Heading>
                <Stack spacing="4" direction="column" align="end">
                  <Text textStyle="caption">
                    Select contacts to receive updates and booking confirmations in addition to the primary request
                    author. These contacts will be CC'd in future emails based on the chosen preferences below.
                  </Text>
                  <SimpleFieldArray
                    name="default_contacts"
                    addLabel="Add Contact"
                    form={({ prefix }) => (
                      <Stack direction="row" spacing="4" justify="stretch">
                        <SimpleReactSelect
                          variant="outline"
                          name={`${prefix}.contact_id`}
                          label="Contact"
                          options={contactOptions}
                        />
                        <SimpleReactSelect
                          variant="outline"
                          name={`${prefix}.updates_for`}
                          label="Email Preferences"
                          options={notificationCategoryOptions}
                          isMulti
                        />
                      </Stack>
                    )}
                  />
                </Stack>
              </SimpleForm>
            </TabPanel>
            <TabPanel>
              <SimpleForm {...methods} submitOnBlur={false}>
                <Heading variant="h5">Provider Accounts</Heading>
                <Stack spacing="4" direction="column" align="start">
                  <Text textStyle="caption">Add provider account numbers to associate with this account.</Text>
                  <SimpleFieldArray
                    name="provider_accounts"
                    addLabel="Add Provider Data"
                    form={({ prefix }) => (
                      <Stack direction="column" spacing="4" justify="stretch" align="center">
                        {/* Select for airlines */}
                        <SimpleReactSelect
                          name={`${prefix}.provider_id`}
                          label="Provider"
                          options={allProviders?.map((provider) => ({
                            label: provider?.name,
                            value: String(provider?.id),
                          }))}
                          isSearchable
                        />
                        <SimpleInput
                          name={`${prefix}.provider_internal_account_id`}
                          label="Provider Internal Account ID"
                        />
                        <SimpleSwitch name={`${prefix}.is_blacklisted`} label="Blacklisted" variant="outline" />
                        <SimpleSwitch name={`${prefix}.has_account`} label="Has Account" variant="outline" />
                        <SimpleInput name={`${prefix}.id`} hidden />
                      </Stack>
                    )}
                  />
                </Stack>
              </SimpleForm>
            </TabPanel>
            <Show if={showApiTab}>
              <TabPanel>
                <Stack divider={<Divider />} spacing="8">
                  <ApiKeysSection accountId={accountId as number} />
                  <AccountWebhooksSection accountId={accountId as number} isDrawerOpen={isOpen} />
                </Stack>
              </TabPanel>
            </Show>
          </TabPanels>
        </Tabs>
      </DrawerBody>
      <DrawerFooter borderTopWidth="1px">
        <Stack direction="row" justify="space-between" align="center" w="full" spacing="2">
          <Show if={!!accountId}>
            <Box minW="fit-content" alignSelf="flex-end">
              <Text>ID: {accountId}</Text>
            </Box>
          </Show>

          <Show if={tabIndex === 0}>
            <Button
              type="submit"
              colorScheme="brand"
              isDisabled={!canSave}
              isLoading={isCreating || isUpdating}
              onClick={handleSubmit}
            >
              {copy}
            </Button>
          </Show>
          <Show if={tabIndex === 1}>
            <SimpleForm {...inviteMethods} submitOnBlur={false}>
              <Stack as={'form'} direction="row" align="flex-end" w="full" spacing="2" onSubmit={handleCreateInvite}>
                <SimpleInput name="email" label="Email" size="md" />
                <Button type="submit" colorScheme="brand" isLoading={isInviting} isDisabled={!canSave}>
                  Invite
                </Button>
              </Stack>
            </SimpleForm>
          </Show>
          <Show if={[5, 6].includes(tabIndex)}>
            <Button colorScheme="brand" isDisabled={!canSave} onClick={handleSubmit} isLoading={isUpdating}>
              Save
            </Button>
          </Show>
        </Stack>
      </DrawerFooter>
    </BaseDrawer>
  );
};
